/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  SUBSCRIPTION_TIERS,
  WorkspaceFilterColumns,
  WorkspaceFilterModel,
  useSetWorkspaceFilter,
  useValidateWorkspaceListOrder,
  useWorkspaceFilter,
} from '@gripp/shared-logic';
import {
  ColumnSelection,
  DropdownFooter,
  FilterType,
  FormContext,
  filterStyles as styles,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ScrollView, Text, View } from 'react-native';
import { Checkbox } from 'react-native-paper';
import { array, string, object as yupObject } from 'yup';

type WorkspaceFilterProps = {
  onCloseSideBar: () => void;
  type: FilterType;
};

export const WorkspaceFilter = (props: WorkspaceFilterProps) => {
  const { data: workspaceFilter } = useWorkspaceFilter();
  const { mutateAsync: setWorkspaceFilter } = useSetWorkspaceFilter();
  const validateListOrder = useValidateWorkspaceListOrder();

  const formSchema = yupObject({
    columns: array().of(string().optional().nullable()).optional().nullable(),
    subscriptionTiers: array()
      .of(string().optional().nullable())
      .optional()
      .nullable(),
  });
  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      columns: workspaceFilter?.columns,
      subscriptionTiers: workspaceFilter?.subscriptionTiers,
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = formMethods;

  useEffect(() => {
    reset({
      columns: workspaceFilter?.columns,
      subscriptionTiers: workspaceFilter?.subscriptionTiers,
    });
  }, [workspaceFilter, reset]);

  const selectedColumns = watch('columns');
  const selectedTiers = watch('subscriptionTiers');

  const isColumnsFilter = props.type === FilterType.WORKSPACECOLUMNS;
  const isTiersFilter = props.type === FilterType.WORKSPACETIERS;

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...selectedTiers,
      ...(isColumnsFilter && { columns: new WorkspaceFilterModel().columns }),
      ...(isTiersFilter && { subscriptionTiers: undefined }),
    });
  }, [isColumnsFilter, isTiersFilter, reset, selectedColumns, selectedTiers]);

  const canReset = (): boolean => {
    return !Object.values(getValues()).every((value) => value === undefined);
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([
      setWorkspaceFilter(filterModel),
      validateListOrder(filterModel),
    ]);

    props.onCloseSideBar();
  };

  const onReset = async () => {
    const model = new WorkspaceFilterModel();
    const updatedFields = {
      ...(isColumnsFilter && { columns: model.columns }),
      ...(isTiersFilter && { subscriptionTiers: undefined }),
    };

    resetForm();

    const mergedFilters = { ...workspaceFilter, ...updatedFields };
    await applyFilter(new WorkspaceFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    const model = new WorkspaceFilterModel({
      ...workspaceFilter,
      ...data,
    });
    await applyFilter(model);
  };

  const renderStandardData = () => {
    return (
      <ColumnSelection
        baseColumns={WorkspaceFilterColumns}
        i18nPrefix="workspace.filter"
      />
    );
  };

  const renderTierFilter = () => {
    return (
      <Controller
        name="subscriptionTiers"
        control={formMethods.control}
        render={({ field: { value, onChange } }) => {
          const toggleTier = (tierId: string) => {
            const currentTiers = value || [];
            const newTiers = currentTiers.includes(tierId)
              ? currentTiers.filter((id: string) => id !== tierId)
              : [...currentTiers, tierId];
            onChange(newTiers);
          };

          return (
            <View style={styles.markersContainer}>
              {SUBSCRIPTION_TIERS.map((tier) => (
                <View key={tier.id} style={styles.markerItemRow}>
                  <View style={styles.markerLeft}>
                    <Text style={styles.markerLabel}>{tier.name}</Text>
                  </View>
                  <View style={styles.markerRight}>
                    <Checkbox.Item
                      label=""
                      status={
                        (value || []).includes(tier.id)
                          ? 'checked'
                          : 'unchecked'
                      }
                      onPress={() => toggleTier(tier.id)}
                      style={{ paddingVertical: 0 }}
                    />
                  </View>
                </View>
              ))}
            </View>
          );
        }}
      />
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { marginBottom: 30 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isColumnsFilter && renderStandardData()}
            {isTiersFilter && renderTierFilter()}
          </ScrollView>
        </View>
        <DropdownFooter
          isDirty={isDirty}
          onSubmit={handleSubmit(onSubmit)}
          canReset={canReset}
          onReset={onReset}
        />
      </View>
    </FormContext.Provider>
  );
};
